import { ActionTree } from 'vuex'
import scriptsApiService from '@/services/scripts'
import { RootState } from '@/store/state'
import { SCRIPTS__FETCH_ONE } from '@/store/actions'
import { CardBlock, TCardBlockValue, CardOption } from '@/models/scripts'
import {
  ScriptEditMutationPayload,
  ScriptEditMutationTypes,
  ScriptEditSetCardMutationPayload,
  ScriptEditSetBlocksMutationPayload,
  ScriptEditSetBlockValueMutationPayload,
  ScriptEditSetOptionsMutationPayload,
} from './mutations'
import { ScriptEditState } from './state'
import { CardVariant, CardBlockType } from '@/constants/scripts'

export enum ScriptEditActionTypes {
  Edit = '[Scripts Edit Action] Edit',
  GetCard = '[Scripts Edit Action] Get Card',
  UpdateCard = '[Scripts Edit Action] Update Card',
  CreateTest = '[Scripts Edit Action] Create Test',
  DeleteTest = '[Scripts Edit Action] Delete Test',
  AddBlock = '[Scripts Edit Action] Add Block',
  DeleteBlock = '[Scripts Edit Action] Delete Block',
  EditBlock = '[Scripts Edit Action] Edit Block',
  RemoveOption = '[Scripts Edit Action] Remove Option',
  EditBlocksOrder = '[Scripts Edit Action] Edit Blocks Order',
  EditOptionsOrder = '[Scripts Edit Action] Edit Options Order',
}

export type ScriptEditActionPayload = {
  cardId: string
  scriptId: string
  instanceId: string
  variant?: CardVariant
}

export type ScriptEditUpdateCardPayload = {
  name: string
}

export type ScriptEditAddBlockPayload = {
  type: CardBlockType
} & Partial<TCardBlockValue>

export type ScriptEditDeleteBlockPayload = {
  blockId: string
}

export type ScriptEditEditBlockPayload = {
  blockId: string
  value: TCardBlockValue
}

export type ScriptEditRemoveOptionPayload = {
  optionId: string
}

export type ScriptEditBlockOrderPayload = {
  selectedItem: CardBlock
  items: CardBlock[]
}

export type ScriptEditOptionOrderPayload = {
  selectedItem: CardOption
  items: CardOption[]
}

export const actions: ActionTree<ScriptEditState, RootState> = {
  async [ScriptEditActionTypes.Edit](
    context,
    payload: ScriptEditActionPayload,
  ) {
    const { cardId, scriptId, instanceId, variant } = payload

    if (context.getters.getScriptById(scriptId) === undefined) {
      await context.dispatch(SCRIPTS__FETCH_ONE, { id: scriptId })
    }

    const editPayload: ScriptEditMutationPayload = {
      cardId,
      scriptId,
      instanceId,
      variant,
    }
    context.commit(ScriptEditMutationTypes.Edit, editPayload)
    return context.dispatch(ScriptEditActionTypes.GetCard)
  },

  async [ScriptEditActionTypes.GetCard](context) {
    const cardId = context.getters.getScriptEditCardId
    const scriptId = context.getters.getScriptEditId
    const instanceId = context.getters.getScriptEditInstanceId

    const response = await scriptsApiService.getScriptCardContent(
      scriptId,
      cardId,
      instanceId,
    )

    const { data: card } = response

    const setCardPayload: ScriptEditSetCardMutationPayload = { card }

    context.commit(ScriptEditMutationTypes.SetCard, setCardPayload)

    return response
  },

  async [ScriptEditActionTypes.UpdateCard](
    context,
    payload: ScriptEditUpdateCardPayload,
  ) {
    const { name } = payload
    const cardId = context.getters.getScriptEditCardId
    const scriptId = context.getters.getScriptEditId

    const response = await scriptsApiService.updateScriptCard(
      scriptId,
      cardId,
      name,
    )

    context.dispatch(ScriptEditActionTypes.GetCard, { scriptId, cardId })

    return response
  },

  async [ScriptEditActionTypes.CreateTest](context) {
    const cardId = context.getters.getScriptEditCardId
    const scriptId = context.getters.getScriptEditId
    const instanceId = context.getters.getScriptEditInstanceId

    const response = await scriptsApiService.createTest(scriptId, cardId)

    if (response.data) {
      const payload: ScriptEditActionPayload = {
        cardId,
        scriptId,
        instanceId,
        variant: CardVariant.B,
      }
      return context.dispatch(ScriptEditActionTypes.Edit, payload)
    }
  },

  async [ScriptEditActionTypes.DeleteTest](context) {
    const cardId = context.getters.getScriptEditCardId
    const scriptId = context.getters.getScriptEditId
    const instanceId = context.getters.getScriptEditInstanceId
    const variant = context.getters.getScriptEditCardVariant

    const response = await scriptsApiService.deleteTest(
      scriptId,
      cardId,
      variant,
    )

    if (response.data) {
      const updateCardPayload: ScriptEditActionPayload = {
        cardId,
        scriptId,
        instanceId,
      }
      return context.dispatch(ScriptEditActionTypes.Edit, updateCardPayload)
    }
  },

  async [ScriptEditActionTypes.AddBlock](
    context,
    payload: ScriptEditAddBlockPayload,
  ): Promise<CardBlock | void> {
    const { type, val } = payload

    const cardId = context.getters.getScriptEditCardId
    const scriptId = context.getters.getScriptEditId
    const instanceId = context.getters.getScriptEditInstanceId
    const variant = context.getters.getScriptEditCardVariant

    const response = await scriptsApiService.createBlock(scriptId, cardId, {
      type,
      variant,
      val,
    })

    if (response.data) {
      const updateCardPayload: ScriptEditActionPayload = {
        cardId,
        scriptId,
        instanceId,
        variant,
      }
      await context.dispatch(ScriptEditActionTypes.Edit, updateCardPayload)
      return response.data
    }
  },

  async [ScriptEditActionTypes.DeleteBlock](
    context,
    payload: ScriptEditDeleteBlockPayload,
  ) {
    const { blockId } = payload

    const cardId = context.getters.getScriptEditCardId
    const scriptId = context.getters.getScriptEditId
    const instanceId = context.getters.getScriptEditInstanceId
    const variant = context.getters.getScriptEditCardVariant

    const response = await scriptsApiService.deleteBlock(
      scriptId,
      cardId,
      blockId,
    )

    if (response.data) {
      const updateCardPayload: ScriptEditActionPayload = {
        cardId,
        scriptId,
        instanceId,
        variant,
      }
      return context.dispatch(ScriptEditActionTypes.Edit, updateCardPayload)
    }
  },

  async [ScriptEditActionTypes.EditBlock](
    context,
    payload: ScriptEditEditBlockPayload,
  ) {
    const { blockId, value } = payload

    const scriptId = context.getters.getScriptEditId
    const cardId = context.getters.getScriptEditCardId
    const variant = context.getters.getScriptEditCardVariant as CardVariant

    const updateBlockValuePayload: ScriptEditSetBlockValueMutationPayload = {
      value,
      cardId,
      blockId,
      variant,
    }
    context.commit(
      ScriptEditMutationTypes.SetBlockValue,
      updateBlockValuePayload,
    )

    return await scriptsApiService.editBlock(scriptId, cardId, blockId, value)
  },

  async [ScriptEditActionTypes.RemoveOption](
    context,
    payload: ScriptEditRemoveOptionPayload,
  ) {
    const { optionId } = payload

    const cardId = context.getters.getScriptEditCardId
    const scriptId = context.getters.getScriptEditId
    const instanceId = context.getters.getScriptEditInstanceId
    const variant = context.getters.getScriptEditCardVariant

    const response = await scriptsApiService.deleteOption(
      scriptId,
      cardId,
      optionId,
    )

    if (response.data) {
      const updateCardPayload: ScriptEditActionPayload = {
        cardId,
        scriptId,
        instanceId,
        variant,
      }
      return context.dispatch(ScriptEditActionTypes.Edit, updateCardPayload)
    }
  },

  async [ScriptEditActionTypes.EditBlocksOrder](
    context,
    payload: ScriptEditBlockOrderPayload,
  ) {
    const cardId = context.getters.getScriptEditCardId
    const scriptId = context.getters.getScriptEditId
    const instanceId = context.getters.getScriptEditInstanceId
    const variant = context.getters.getScriptEditCardVariant as CardVariant
    const { selectedItem, items } = payload

    const updateBlocksPayload: ScriptEditSetBlocksMutationPayload = {
      blocks: items,
      cardId,
      variant,
    }
    context.commit(ScriptEditMutationTypes.SetBlocks, updateBlocksPayload)

    await scriptsApiService.editBlockNumber(
      scriptId,
      cardId,
      selectedItem.id as string,
      selectedItem.n,
    )

    const updateCardPayload: ScriptEditActionPayload = {
      cardId,
      scriptId,
      instanceId,
      variant,
    }
    return context.dispatch(ScriptEditActionTypes.Edit, updateCardPayload)
  },

  async [ScriptEditActionTypes.EditOptionsOrder](
    context,
    payload: ScriptEditOptionOrderPayload,
  ) {
    const scriptId = context.getters.getScriptEditId
    const cardId = context.getters.getScriptEditCardId
    const variant = context.getters.getScriptEditCardVariant as CardVariant
    const { selectedItem, items } = payload

    const updateOptionsPayload: ScriptEditSetOptionsMutationPayload = {
      options: items,
      cardId,
      variant,
    }
    context.commit(ScriptEditMutationTypes.SetOptions, updateOptionsPayload)

    await scriptsApiService.editOptionNumber(
      scriptId,
      cardId,
      selectedItem.id as string,
      selectedItem.n,
    )
  },
}
